/**
 * Main data hook for creating chats.
 *
 * Orchestrates the two-phase create flow and returns a discriminated
 * `CreateChatResult` (see `@choco-admin/types`). It performs NO user-facing
 * side effects — no toasts, no navigation. That keeps ownership of those in a
 * single upper layer (`useCreateChatForm`) and makes the create-safety contract
 * reusable for follow-up work (create-time config, #125).
 *
 * Phase 1 (`chatCreate`): a failure here means no chat exists — the caller may
 * safely retry.
 * Phase 2 (post-create configuration): the chat already exists, so a failure is
 * reported as `partial` (with the created chat id preserved AND the exact
 * `failedSetting` named) and must never be surfaced as a create failure that
 * invites a blind retry. The settings applied here (#125) run in a fixed order —
 * price visibility, custom catalog, delivery settings, eCommerce PO number,
 * notification preferences — each reusing the same explicit mutation the Edit
 * tabs use, run silently so this layer owns no toasts. The first failure short-
 * circuits the remaining steps and is returned as `partial`.
 */

import { useApolloClient } from '@apollo/client';
import type {
  CreateChatConfig,
  CreateChatFormData,
  CreateChatResult,
  CreatedChatResponse,
} from '@choco-admin/types';
import { CreateChatType, PostCreateSetting } from '@choco-admin/types';
import { useCallback, useRef, useState } from 'react';

import { useCreateChatMutation } from '../../../generated';
import { usePuppyMode } from '../../../hooks';
import { ChatsPageGetSupplierBotUserQuery } from '../data/queries/getSupplierBotUser.gql';
import {
  mapToCreatedChatResponse,
  mapToGraphQLChatCreateInput,
  extractUserIds,
} from '../utils/createChatMapper';
import { getSubCatalogChanges } from '../utils/subCatalogSelection';

import { useSetChatNotificationPreferences } from './useSetChatNotificationPreferences';
import { useUpdateChatOrderingSettings } from './useUpdateChatOrderingSettings';
import { useUpdateChatPricesVisibility } from './useUpdateChatPricesVisibility';
import { useUpdateDeliverySetting } from './useUpdateDeliverySetting';
import { useUpdateSubCatalogChats } from './useUpdateSubCatalogChats';

/** Shape of the ChatsPageGetSupplierBotUser query response */
interface ChatsPageGetSupplierBotUserData {
  supplier?: {
    id: string;
    botUser?: {
      id: string;
    } | null;
  } | null;
}

/** Variables for the ChatsPageGetSupplierBotUser query */
interface ChatsPageGetSupplierBotUserVariables {
  id: string;
}

export interface UseCreateChatResponse {
  createChat: (formData: CreateChatFormData) => Promise<CreateChatResult>;
  isLoading: boolean;
  /** Set only when `chatCreate` itself fails (Phase 1). */
  error: Error | null;
}

/** Narrow a thrown value to an Error without an `as` cast. */
const toError = (value: unknown): Error =>
  value instanceof Error ? value : new Error(String(value));

export const useCreateChat = (
  config: CreateChatConfig = {}
): UseCreateChatResponse => {
  const { captureException } = config;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { puppyFilter } = usePuppyMode();
  const apolloClient = useApolloClient();

  // Post-create configuration failures are captured here. Every post-create
  // hook runs `silent` so it fires no toast of its own — the form layer owns all
  // create notifications — and reports its failure through a shared ref that the
  // orchestration reads after each step.
  const postCreateErrorRef = useRef<Error | null>(null);
  const capturePostCreateError = useCallback((postError: Error) => {
    postCreateErrorRef.current = postError;
  }, []);

  // Generated GraphQL mutation hooks
  const [createChatMutation] = useCreateChatMutation();
  const { updatePricesVisibility } = useUpdateChatPricesVisibility({
    silent: true,
    onError: capturePostCreateError,
  });
  const { updateSubCatalogChats } = useUpdateSubCatalogChats({
    silent: true,
    onError: capturePostCreateError,
  });
  const { updateSettings: updateDeliverySettings } = useUpdateDeliverySetting({
    silent: true,
    onError: capturePostCreateError,
  });
  const { updateOrderingSettings } = useUpdateChatOrderingSettings({
    silent: true,
    onError: capturePostCreateError,
  });
  const { setNotificationPreferences } = useSetChatNotificationPreferences({
    onError: capturePostCreateError,
  });

  const createChat = useCallback(
    async (formData: CreateChatFormData): Promise<CreateChatResult> => {
      setIsLoading(true);
      setError(null);

      // ----- Phase 1: create the chat (a failure here means no chat exists) --
      let createdChat: CreatedChatResponse;
      // Resolved in Phase 1 and reused by the notification step in Phase 2 (the
      // bot user leads the chat's distributor users).
      let supplierBotUserId: string | null = null;
      try {
        // Step 1: Resolve the supplier's bot user ID.
        //
        // chat-service's legacy `chatCreate` handler requires `supplierId` to be
        // a supplier USER id — it calls getUserByIdHttp(supplierId) internally
        // and resolves the entity's botUserId from there. The distributor picker
        // yields the ENTITY id, so we fetch the botUserId before building the
        // payload. The entity id is still sent as `supplierEntityId`.
        const supplierEntityId = formData.supplier?.value ?? null;

        if (supplierEntityId) {
          const { data: botUserData } = await apolloClient.query<
            ChatsPageGetSupplierBotUserData,
            ChatsPageGetSupplierBotUserVariables
          >({
            query: ChatsPageGetSupplierBotUserQuery,
            variables: { id: supplierEntityId },
            fetchPolicy: 'network-only',
          });

          supplierBotUserId = botUserData?.supplier?.botUser?.id ?? null;

          if (!supplierBotUserId) {
            throw new Error(
              'This distributor has no bot user — chat creation unavailable'
            );
          }
        }

        // Step 2: Build userIds. The legacy convention requires the supplier bot
        // user id to lead the list (chat-service correlates userIds[0] with
        // supplierId). Supplier/buyer user selections follow.
        const userIds = extractUserIds(formData);
        // Replace the leading entity id with the resolved bot user id so the
        // list matches the legacy contract.
        if (supplierBotUserId && userIds.length > 0) {
          userIds[0] = supplierBotUserId;
        }

        // Step 3: Build mutation input.
        // `supplierId` must be the supplier USER (bot user) id, NOT the entity id.
        // `supplierEntityId` carries the entity id for reference lookups.
        const chatCreateInput = {
          ...mapToGraphQLChatCreateInput({
            chatName: formData.chatName,
            restaurantName: formData.restaurantName,
            deliveryAddress: formData.deliveryAddress,
            customerNumber: formData.customerNumber,
            additionalEmails: formData.additionalEmails,
            // Chats-first launch: all admin-created chats are ordering chats,
            // regardless of what the (disabled) type select holds.
            type: CreateChatType.ORDERING,
            // Pass the resolved bot user id as supplierId (legacy field).
            // mapToGraphQLChatCreateInput sets both supplierId and supplierEntityId
            // from this value; we override supplierEntityId below to preserve the
            // entity id.
            supplierId: supplierBotUserId ?? supplierEntityId,
            buyerId: formData.buyer?.value,
            userIds,
            createdContext: formData.createdContext?.value,
          }),
          // Restore the entity id that mapToGraphQLChatCreateInput overwrote.
          supplierEntityId,
          isPuppy: puppyFilter || false,
        };

        const { data: chatData } = await createChatMutation({
          variables: {
            chat: chatCreateInput,
          },
          refetchQueries: [
            'ChatSearchData', // Refetch chat list
          ],
          awaitRefetchQueries: true,
        });

        if (!chatData?.chatCreate) {
          throw new Error('Failed to create chat - no data returned');
        }

        createdChat = mapToCreatedChatResponse(chatData.chatCreate);
      } catch (err) {
        const chatError = toError(err);
        setError(chatError);
        captureException?.(chatError);
        setIsLoading(false);
        return { status: 'error', error: chatError };
      }

      // ----- Phase 2: post-create configuration (the chat already exists) -----
      // Each setting is applied through the same explicit mutation the Edit tabs
      // use, run silently. A failure here does NOT fail the creation — the chat
      // already exists — so it is reported as `partial` with the exact setting
      // named and the chat id preserved, and the operator is told not to retry.
      const chatId = createdChat.id;

      // Custom catalog: create-only diff (no existing assignment on a new chat);
      // this filters the "default"/no-catalog sentinel out for us.
      const { createIds: customCatalogIds } = getSubCatalogChanges(
        [],
        (formData.subCatalog ?? []).map((option) => option.value)
      );

      // Notification preferences: only the distributor users actually in the new
      // chat can be deselected. The bot user leads the list; explicitly added
      // distributor users follow. Buyer-side selections are never in scope.
      const chatDistributorUserIds = [
        ...(supplierBotUserId ? [supplierBotUserId] : []),
        ...(formData.supplierUsers ?? []).map((user) => user.value),
      ];
      const deselectedUsersList = (
        formData.deselectedOrderPreferences ?? []
      ).filter((id) => chatDistributorUserIds.includes(id));

      const steps: Array<{
        setting: PostCreateSetting;
        run: () => Promise<void>;
      }> = [];

      if (formData.isPriceOverrideEnabled) {
        steps.push({
          setting: PostCreateSetting.PRICE_VISIBILITY,
          run: () =>
            updatePricesVisibility({
              chatId,
              displayPricesOverride: formData.visibleChatPrices ?? true,
            }),
        });
      }
      if (customCatalogIds.length > 0) {
        steps.push({
          setting: PostCreateSetting.CUSTOM_CATALOG,
          run: () =>
            updateSubCatalogChats({
              chatId,
              createIds: customCatalogIds,
              removeIds: [],
            }),
        });
      }
      if (formData.hasCustomChatDeliverySettings) {
        steps.push({
          setting: PostCreateSetting.DELIVERY_SETTINGS,
          run: () =>
            updateDeliverySettings({
              chatId,
              deliveryDays: formData.deliveryDays,
              cutoffType: formData.cutoffType,
              cutoffTimes: formData.cutoffTimes,
              cutoffDays: formData.cutoffDays,
            }),
        });
      }
      if (formData.isEcommercePoNumberRequired) {
        steps.push({
          setting: PostCreateSetting.PO_NUMBER,
          run: () =>
            updateOrderingSettings({
              chatId,
              isEcommercePoNumberRequired: true,
            }),
        });
      }
      if (deselectedUsersList.length > 0) {
        steps.push({
          setting: PostCreateSetting.NOTIFICATION_PREFERENCES,
          run: () =>
            setNotificationPreferences({ chatId, deselectedUsersList }),
        });
      }

      // Run the steps strictly in order, short-circuiting on the first failure.
      // Recursion (not a loop) keeps this sequential await lint-clean. Some
      // silent hooks report via `onError` (which sets the ref) and resolve
      // rather than throw; the try/catch is a safety net for an unexpected
      // throw. The ref is reset before each step so a prior success never leaks.
      const runStep = async (
        index: number
      ): Promise<CreateChatResult | null> => {
        if (index >= steps.length) {
          return null;
        }
        const step = steps[index];
        postCreateErrorRef.current = null;
        try {
          await step.run();
        } catch (err) {
          postCreateErrorRef.current = toError(err);
        }

        const postCreateError = postCreateErrorRef.current;
        if (postCreateError) {
          captureException?.(postCreateError);
          return {
            status: 'partial',
            chat: createdChat,
            failedSetting: step.setting,
            postCreateError,
          };
        }
        return runStep(index + 1);
      };

      const partialResult = await runStep(0);
      setIsLoading(false);
      return partialResult ?? { status: 'success', chat: createdChat };
    },
    [
      apolloClient,
      createChatMutation,
      updatePricesVisibility,
      updateSubCatalogChats,
      updateDeliverySettings,
      updateOrderingSettings,
      setNotificationPreferences,
      puppyFilter,
      captureException,
    ]
  );

  return {
    createChat,
    isLoading,
    error,
  };
};
